import { Link, usePage } from "@inertiajs/react";
import { BookOpen, Clock, TrendingUp, Users, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";
import StatTile from "@/components/shared/StatTile";
import ProgressBar from "@/components/shared/ProgressBar";
import { formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber, percentOf } from "@/lib/format";
import { formatPhaseDate, formatPhaseTime, phaseAcceptsMinutes } from "@/lib/campaignPhase";

/**
 * The top of the teacher's dashboard: how their class is doing, what's
 * happening in the campaign right now, and the one thing they do most —
 * log the class's reading time.
 */
export default function ClassroomTodayCard({ classroom, campaign, phase, log_minutes_path, goals_path }) {
  const { auth } = usePage().props;
  // Co-teachers only — the teacher knows they teach it.
  const coTeachers = classroom.teachers.filter((name) => name !== auth?.user?.display_name);
  const canLog = phaseAcceptsMinutes(phase) && campaign.accepting_minutes;
  const hasGoal = classroom.minute_goal > 0 || classroom.money_goal_cents > 0;
  const minutePct = percentOf(classroom.total_minutes_logged, classroom.minute_goal);
  const moneyPct = percentOf(classroom.total_raised_cents, classroom.money_goal_cents);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Your class</CardTitle>
        <CardDescription>
          {[
            classroom.grade && formatGrade(classroom.grade),
            coTeachers.length > 0 && `with ${coTeachers.join(" and ")}`,
          ].filter(Boolean).join(" · ")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <PhaseNotice phase={phase} campaign={campaign} />

        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <StatTile icon={BookOpen} label="Minutes" value={formatNumber(classroom.total_minutes_logged)} size="sm" />
          <StatTile icon={TrendingUp} label="Raised" value={formatMoney(classroom.total_raised_cents)} color="primary" size="sm" />
          <StatTile icon={Users} label="Students" value={formatNumber(classroom.student_count)} size="sm" />
        </div>

        {hasGoal ? (
          <div className="space-y-4">
            {classroom.minute_goal > 0 && (
              <ProgressBar
                label="Class reading goal"
                detail={`${formatNumber(classroom.total_minutes_logged)} / ${formatNumber(classroom.minute_goal)} min`}
                pct={minutePct}
                barColor="bg-info"
              />
            )}
            {classroom.money_goal_cents > 0 && (
              <ProgressBar
                label="Class fundraising goal"
                detail={`${formatMoney(classroom.total_raised_cents)} / ${formatMoney(classroom.money_goal_cents)}`}
                pct={moneyPct}
              />
            )}
          </div>
        ) : (
          phase !== "finished" && goals_path && (
            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Target className="h-4 w-4 shrink-0" />
                No class goal yet. A reading goal gives the class a bar to fill together.
              </span>
              <Link href={goals_path} className="shrink-0 font-medium text-primary hover:underline">Set a goal</Link>
            </div>
          )
        )}

        {canLog && (
          <Button size="lg" className="w-full" asChild>
            <Link href={log_minutes_path}>
              <Clock className="h-4 w-4" />
              Log minutes
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// What's happening in the campaign, said for teachers. Nothing while
// reading is under way — the Log minutes button says it all.
function PhaseNotice({ phase, campaign }) {
  switch (phase) {
    case "getting_ready":
      return (
        <Notice>
          Reading starts {formatPhaseDate(campaign.start_at)} and you can log minutes from then. Before that, make sure every student takes home their sheet.
        </Notice>
      );
    case "donations_open":
      return (
        <Notice>
          Donations are open and reading starts {formatPhaseDate(campaign.start_at)}. You can log minutes from then.
        </Notice>
      );
    case "catch_up":
      return (
        <Notice tone="warning" icon={Clock}>
          Reading has ended. Log any final minutes before {formatPhaseTime(campaign.minutes_close_at)}.
        </Notice>
      );
    case "wrapping_up":
      return (
        <Notice>
          Minute logging closed at {formatPhaseTime(campaign.minutes_close_at)}. You can still fix or undo a log until {formatPhaseDate(campaign.grace_ends_at)}, when the read-a-thon wraps up.
        </Notice>
      );
    case "finished":
      return <Notice tone="success">This read-a-thon is wrapped up. Final totals below.</Notice>;
    default:
      return null;
  }
}
